/**
 * Technical Analysis data service
 * Fetches indicators from TAAPI.IO (crypto + equities) and Fear & Greed
 */

const TAAPI_BASE = "https://api.taapi.io";

export type AssetType = "crypto" | "equity";

export interface TAData {
  symbol: string;
  type: AssetType;
  interval: string;
  indicators: Record<string, number | { [k: string]: unknown }>;
  fearGreed?: number;
  fetchedAt: string;
}

const CRYPTO_SYMBOLS = ["BTC/USDT", "ETH/USDT", "SOL/USDT", "BNB/USDT"];
const EQUITY_SYMBOLS = ["MSTR", "AAPL", "TSLA", "NVDA", "COIN", "SPY", "QQQ"];

export function isCrypto(symbol: string): boolean {
  return symbol.includes("/") || CRYPTO_SYMBOLS.some((s) => s.startsWith(symbol));
}

export function isEquity(symbol: string): boolean {
  const upper = symbol.toUpperCase();
  return EQUITY_SYMBOLS.includes(upper) || /^[A-Z]{1,5}$/.test(upper);
}

export function normalizeSymbol(symbol: string): string {
  const s = symbol.trim().toUpperCase();
  if (s.includes("/")) return s;
  if (["BTC", "ETH", "SOL", "BNB"].includes(s)) return `${s}/USDT`;
  return s;
}

async function fetchTAAPI(
  indicator: string,
  params: Record<string, string>
): Promise<number | Record<string, unknown>> {
  const secret = process.env.REACT_APP_TAAPI_SECRET;
  if (!secret || secret === "your_taapi_secret_here") {
    throw new Error("REACT_APP_TAAPI_SECRET not configured");
  }

  const q = new URLSearchParams({ ...params, secret });
  const res = await fetch(`${TAAPI_BASE}/${indicator}?${q}`);
  const json = await res.json();

  if (json.value !== undefined) return json.value as number;
  if (json.valueMACD !== undefined) {
    return {
      valueMACD: json.valueMACD,
      valueMACDSignal: json.valueMACDSignal,
      valueMACDHist: json.valueMACDHist,
    };
  }
  throw new Error(json.message || "Invalid TAAPI response");
}

async function fetchFearGreed(): Promise<number> {
  const res = await fetch("https://api.alternative.me/fng/?limit=1");
  const json = await res.json();
  return parseInt(json.data?.[0]?.value ?? "50", 10);
}

export async function fetchTAData(
  symbol: string,
  interval = "1h"
): Promise<TAData> {
  const normalized = normalizeSymbol(symbol);
  const isCryptoAsset = isCrypto(normalized);

  const baseParams: Record<string, string> = isCryptoAsset
    ? { exchange: "binance", symbol: normalized, interval }
    : { type: "stocks", symbol: normalized, interval };

  const [rsi, price, fearGreed] = await Promise.all([
    fetchTAAPI("rsi", baseParams).catch(() => null),
    fetchTAAPI("price", baseParams).catch(() => null),
    isCryptoAsset ? fetchFearGreed().catch(() => undefined) : Promise.resolve(undefined),
  ]);

  const indicators: TAData["indicators"] = {};
  if (typeof rsi === "number") indicators.rsi = rsi;
  if (typeof price === "number") indicators.price = price;

  return {
    symbol: normalized,
    type: isCryptoAsset ? "crypto" : "equity",
    interval,
    indicators,
    fearGreed,
    fetchedAt: new Date().toISOString(),
  };
}

export function formatTAContext(data: TAData): string {
  const lines: string[] = [
    `Symbol: ${data.symbol} (${data.type})`,
    `Interval: ${data.interval}`,
    `Fetched: ${data.fetchedAt}`,
  ];
  if (data.indicators.rsi !== undefined) {
    lines.push(`RSI: ${data.indicators.rsi}`);
  }
  if (data.fearGreed !== undefined) {
    lines.push(`Fear & Greed Index: ${data.fearGreed}`);
  }
  return lines.join("\n");
}
